import { Suspense, useEffect, type ReactNode } from 'react'
import { Routes, Route, useSearchParams, useLocation, useParams, type Location } from 'react-router-dom'
import AppShell from './components/AppShell'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import { lazyWithRetry } from './lib/lazyWithRetry'
import AbuseOverlay from './components/AbuseOverlay'
import EmbeddedShell from './components/EmbeddedShell'
import RequireAuth from './components/RequireAuth'
import RequireCredits from './components/RequireCredits'
import OptionalAuth from './components/OptionalAuth'
import { Navigate } from './components/nav'
import { EmbedContext, isEmbeddedPath, useEmbedded } from './lib/embed'
import Dashboard from './pages/Dashboard'
import Projects from './pages/Projects'
import ProjectDetail from './pages/ProjectDetail'
import ProjectOverview from './pages/ProjectOverview'
import InvitePage from './pages/InvitePage'
import JoinTeamPage from './pages/JoinTeamPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import TestRunSessionDetail from './pages/TestRunSessionDetail'
import JourneyDetail from './pages/JourneyDetail'
import UserSessionAnalysis from './pages/UserSessionAnalysis'
import SessionReviewerDetail from './pages/SessionReviewerDetail'
import BugDetail from './pages/BugDetail'
import TaskPage from './pages/TaskPage'
import ReleaseNotes from './pages/ReleaseNotes'
import ProcessHistory from './pages/ProcessHistory'
import { useExperimentalFlag } from './hooks/useExperimentalFlag'
import { trackPageview } from './lib/analytics'
import { setFullStoryPage } from './lib/fullstory'
import { describePage } from './lib/pageIdentity'
import SegmentAuthBridge from './components/SegmentAuthBridge'
import FullStoryAuthBridge from './components/FullStoryAuthBridge'
import PostHogAuthBridge from './components/PostHogAuthBridge'
import OutOfCreditsModal from './components/OutOfCreditsModal'
import WelcomeModal from './components/WelcomeModal'

const Admin = lazyWithRetry(() => import('./pages/Admin'))
const AdminUsers = lazyWithRetry(() => import('./pages/AdminUsers'))
const AdminUserDetail = lazyWithRetry(() => import('./pages/AdminUserDetail'))
const AdminActivityDetail = lazyWithRetry(() => import('./pages/AdminActivityDetail'))
const AdminActivityErrors = lazyWithRetry(() => import('./pages/AdminActivityErrors'))
const AdminEventDetail = lazyWithRetry(() => import('./pages/AdminEventDetail'))
const AdminErrorDetail = lazyWithRetry(() => import('./pages/AdminErrorDetail'))
const AdminErrorBuckets = lazyWithRetry(() => import('./pages/AdminErrorBuckets'))
const AdminErrorBucketDetail = lazyWithRetry(() => import('./pages/AdminErrorBucketDetail'))
const AdminBugDistribution = lazyWithRetry(() => import('./pages/AdminBugDistribution'))
const AdminEvals = lazyWithRetry(() => import('./pages/AdminEvals'))
const AdminEvalDetail = lazyWithRetry(() => import('./pages/AdminEvalDetail'))
const AdminCritics = lazyWithRetry(() => import('./pages/AdminCritics'))
const AdminCriticDetail = lazyWithRetry(() => import('./pages/AdminCriticDetail'))
const AdminCriticDay = lazyWithRetry(() => import('./pages/AdminCriticDay'))
const AdminTierUsers = lazyWithRetry(() => import('./pages/AdminTierUsers'))
const AdminProjectDetail = lazyWithRetry(() => import('./pages/AdminProjectDetail'))
const AdminCostAttribution = lazyWithRetry(() => import('./pages/AdminCostAttribution'))
const AdminReactivationEmails = lazyWithRetry(() => import('./pages/AdminReactivationEmails'))
const AdminAnalysis = lazyWithRetry(() => import('./pages/AdminAnalysis'))
const AdminAggregates = lazyWithRetry(() => import('./pages/AdminAggregates'))
// The Insights page is an experimental, opt-in surface (Settings > Experimental): the route below
// redirects everyone without the `insights` flag back to /projects, yet the static import here
// dragged the page and its recharts dependency into the eager entry bundle every first load parses.
// Load it as a deferred route chunk like the Admin pages instead (QA bug bug-mtz7miwk-xrnn).
const Insights = lazyWithRetry(() => import('./pages/Insights'))
// The Playwright failure-analysis detail page renders only at the deep
// `:projectId/playwright-tests/:analysisId` URL, so it loads as a deferred chunk fetched on first
// visit instead of riding the eager entry bundle every first load parses
// (QA bug bug-mu8z8bor-g25v).
const PlaywrightFailureDetail = lazyWithRetry(() => import('./pages/PlaywrightFailureDetail'))

function AdminRoute({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return <RequireAuth>
    <ChunkErrorBoundary key={pathname} eventTag="admin-route" fallback={
      <div role="alert">
        <p>Couldn’t load this admin page. Reload to get the latest version.</p>
        <button type="button" onClick={() => window.location.reload()}>Reload page</button>
      </div>
    }>
      <Suspense fallback={null}>{children}</Suspense>
    </ChunkErrorBoundary>
  </RequireAuth>
}

// Like AdminRoute, but for the project-scoped Playwright analysis deep link: the page loads as a
// deferred chunk, so a stale-deploy chunk 404 or a network blip needs the same inline reload
// fallback instead of a blank page. OptionalAuth (not RequireAuth) keeps the route's existing
// signed-out access — it is a public deep link.
function PlaywrightAnalysisRoute() {
  const { pathname } = useLocation()
  return <OptionalAuth>
    <ChunkErrorBoundary key={pathname} eventTag="playwright-analysis-route" fallback={
      <div role="alert">
        <p>Couldn’t load this analysis. Reload to get the latest version.</p>
        <button type="button" onClick={() => window.location.reload()}>Reload page</button>
      </div>
    }>
      <Suspense fallback={null}><PlaywrightFailureDetail /></Suspense>
    </ChunkErrorBoundary>
  </OptionalAuth>
}

// `/github/setup` was the GitHub App's post-install landing, and had its own project-creation form —
// a second way to create a project that skipped the onboarding chat entirely (so a project made there
// started with no chat history). The new-project screen is now the landing: it claims the arriving
// installation and resumes the conversation. Keep the install params on the way through; they are what
// identifies the installation to claim.
function GithubSetupRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/new${search}`} replace />
}

function ProjectDetailTabRedirect() {
  const { projectId } = useParams<{ projectId: string }>()
  // `<Navigate>` here is the embed-aware one, so this resolves to `/p/...` inside the embed.
  return projectId ? <Navigate to={`/projects/${projectId}/overview`} replace /> : <Navigate to="/projects" replace />
}

// The full app renders the unified project view (ProjectOverview) for the visible project tabs. The
// classic tabbed ProjectDetail handles any other tab. The former Journeys collection redirects to
// Test Runs in both layouts; individual journey-detail URLs remain valid and are matched first.
function ProjectTabRoute() {
  const { projectId, tab } = useParams<{ projectId: string; tab: string }>()
  const embedded = useEmbedded()
  if (tab === 'journeys') {
    return projectId ? <Navigate to={`/projects/${projectId}/test-runs`} replace /> : <Navigate to="/projects" replace />
  }
  if (!embedded && (tab === 'site-map' || tab === 'overview' || tab === 'bugs' || tab === 'test-runs' || tab === 'user-sessions' || tab === 'playwright-tests' || tab === 'security')) return <ProjectOverview />
  return <ProjectDetail />
}

// Insights is behind the `insights` experimental flag (Settings > Experimental), so the sidebar link
// is hidden and this route sends anyone who still reaches the URL — a bookmark, a shared link — back
// to the projects list. It renders nothing until the flag has actually loaded; redirecting on the
// not-yet-known default would bounce a deep-link that the user is in fact entitled to.
function InsightsRoute() {
  const { enabled, loaded } = useExperimentalFlag('insights')
  const { pathname } = useLocation()
  if (!loaded) return null
  if (!enabled) return <Navigate to="/projects" replace />
  // The page loads as a deferred chunk, so a stale-deploy chunk 404 or a network blip needs the
  // same inline reload fallback as the Admin routes instead of a blank page.
  return <ChunkErrorBoundary key={pathname} eventTag="insights-route" fallback={
    <div role="alert">
      <p>Couldn’t load Insights. Reload to get the latest version.</p>
      <button type="button" onClick={() => window.location.reload()}>Reload page</button>
    </div>
  }>
    <Suspense fallback={null}><Insights /></Suspense>
  </ChunkErrorBoundary>
}

/**
 * The project-scoped routes that exist in BOTH route trees. `prefix` is `/projects` for the full
 * app (rendered inside AppShell) and `/p` for the embeddable iframe layout (rendered inside
 * EmbeddedShell). The same page components back both trees — only the surrounding layout differs,
 * and each page adapts its in-frame chrome via `useEmbedded()`.
 */
/** Strip the leftover ?checkout= param that billing redirects carry. Runs once on mount (billing
 *  returns are full-page loads), so the URL stays clean after a Stripe round-trip. */
function StripCheckoutParam() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    if (checkout === 'success' || checkout === 'cancelled') {
      const next = new URL(window.location.href)
      next.searchParams.delete('checkout')
      window.history.replaceState(null, '', next.toString())
    }
  }, [])
  return null
}

function projectRoutes(prefix: '/projects' | '/p') {
  return (
    <>
      <Route path={`${prefix}/:projectId/playwright-tests/:analysisId`} element={<PlaywrightAnalysisRoute />} />
      <Route path={`${prefix}/:projectId/bugs/:bugId`} element={<OptionalAuth><BugDetail /></OptionalAuth>} />
      {/* Canonical (and only) task-detail route — one page, one rendering pipeline for every task kind. */}
      <Route path={`${prefix}/:projectId/tasks/:taskId`} element={<OptionalAuth><TaskPage /></OptionalAuth>} />
      {/* One test run (test session): its exploration, journeys, and bugs. */}
      <Route path={`${prefix}/:projectId/test-runs/:sessionId`} element={<OptionalAuth><TestRunSessionDetail /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId/journeys/:journeyId`} element={<OptionalAuth><JourneyDetail /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId/session-reviewers/:reviewer`} element={<OptionalAuth><SessionReviewerDetail /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId/user-sessions/:sessionId`} element={<OptionalAuth><UserSessionAnalysis /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId/processing-history`} element={<OptionalAuth><ProcessHistory /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId/:tab`} element={<OptionalAuth><ProjectTabRoute /></OptionalAuth>} />
      <Route path={`${prefix}/:projectId`} element={<OptionalAuth><ProjectDetailTabRedirect /></OptionalAuth>} />
    </>
  )
}

export default function App() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const isEmbedded = isEmbeddedPath(location.pathname)

  // Send an analytics pageview on every client-side navigation (and the initial load). Keyed on the
  // full path so query-param changes that the app treats as new views are also captured.
  //
  // FullStory needs telling separately: it derives pages from URLs on its own, which leaves every
  // route it hasn't been given a rule for unsearchable — including the surfaces we most want counted,
  // like the plan picker. Naming the page here makes each one countable in FullStory too.
  useEffect(() => {
    trackPageview(location.pathname + location.search)
    const { pageName, properties } = describePage(location.pathname, location.search)
    setFullStoryPage(pageName, properties)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (searchParams.has('code') || searchParams.has('state')) {
      const cleaned = new URLSearchParams(searchParams)
      cleaned.delete('code')
      cleaned.delete('state')
      setSearchParams(cleaned, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Settings is a modal, but each tab has its own deep-linkable URL (`/settings/<slug>`). Opening it
  // from within the app stashes the originating page in `location.state.backgroundLocation`; when
  // present we render that page behind the modal so navigating to a settings URL doesn't blow away
  // the page the user was on. A cold deep-link (e.g. from an automated email) has no background, so
  // the `/settings/*` routes below render the projects list behind the modal instead.
  const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation

  // The invite landings (reached from the project-invite and team-invite emails, and from a copied
  // team link) are standalone branded pages for a typically logged-out recipient, so they render
  // WITHOUT the app chrome (no sidebar/top bar) — a signed-out invitee shouldn't be greeted by the
  // authed navigation. Like the embedded shell they short-circuit before AppShell.
  const isInvite = location.pathname.startsWith('/invite/')
  const isJoinTeam = location.pathname.startsWith('/join/')
  const isVerifyEmail = location.pathname === '/verify-email'

  const routes = isEmbedded ? (
    <EmbeddedShell>
      <Routes>{projectRoutes('/p')}</Routes>
    </EmbeddedShell>
  ) : isInvite ? (
    <Routes>
      <Route path="/invite/:projectId" element={<InvitePage />} />
    </Routes>
  ) : isJoinTeam ? (
    <Routes>
      <Route path="/join/:token" element={<JoinTeamPage />} />
    </Routes>
  ) : isVerifyEmail ? (
    <Routes>
      <Route path="/verify-email" element={<VerifyEmailPage />} />
    </Routes>
  ) : (
    <AppShell>
      <Routes location={backgroundLocation ?? location}>
        {/* The Dashboard is the "new project" surface (the sidebar "New project" link points here),
            so the out-of-credits gate lives on this creation route — NOT on the viewing pages below.
            Viewing existing projects/insights must work even with no credits; the gate only appears
            when the user actually goes to create something (here, or the New Exploration button). */}
        <Route path="/" element={<RequireCredits><Dashboard /></RequireCredits>} />
        {/* `/new?url=…` deep-links into the new-project form with the App URL prefilled. It is also
            the post-install landing for the GitHub App (`?installation_id=…`): the onboarding chat
            claims the installation and resumes the conversation the install interrupted. */}
        <Route path="/new" element={<RequireCredits><Dashboard /></RequireCredits>} />
        <Route path="/projects" element={<RequireAuth><Projects /></RequireAuth>} />
        {/* The App's Setup URL used to point at a second project-creation form here. Installs already
            in flight (and bookmarks) still arrive at this path until the Setup URL is repointed, so
            forward them — with the install params — to the new-project screen that now handles them. */}
        <Route path="/github/setup" element={<GithubSetupRedirect />} />
        {projectRoutes('/projects')}
        {/* Settings is a modal opened from the user menu, but every tab is deep-linkable at
            `/settings/<slug>` so automated emails (e.g. Settings > Subscription) can link straight
            to it. AppShell reads the `/settings/*` path and opens the modal on the matching tab;
            these routes render the projects list as the page behind the modal for cold deep-links
            (bare `/settings` defaults to the Account page). See SETTINGS_TAB_SLUGS. */}
        <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="/settings/:tab" element={<RequireAuth><Projects /></RequireAuth>} />
        {/* Guidance is admin-only internal tooling (both pages render "Access denied"
            for non-admins), so — like /admin — it's exempt from the out-of-credits gate.
            Otherwise an admin who has run out of credits hits the SubscriptionGate plan
            picker instead of the guidance tool, and the page appears "busted". */}
        <Route path="/insights" element={<RequireAuth><InsightsRoute /></RequireAuth>} />
        {/* Release notes are public-facing product news for all signed-in users. Exempt from the
            out-of-credits gate (like /insights) so an out-of-credits user can still read them. */}
        <Route path="/release-notes" element={<RequireAuth><ReleaseNotes /></RequireAuth>} />
        {/* /admin is intentionally exempt from the out-of-credits gate so internal
            admins are never locked out of admin tooling. */}
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/admin/cost" element={<AdminRoute><AdminCostAttribution /></AdminRoute>} />
        <Route path="/admin/reactivation-emails" element={<AdminRoute><AdminReactivationEmails /></AdminRoute>} />
        <Route path="/admin/analysis" element={<AdminRoute><AdminAnalysis /></AdminRoute>} />
        <Route path="/admin/aggregates" element={<AdminRoute><AdminAggregates /></AdminRoute>} />
        <Route path="/admin/activity" element={<AdminRoute><AdminActivityDetail /></AdminRoute>} />
        <Route path="/admin/events" element={<AdminRoute><AdminEventDetail /></AdminRoute>} />
        <Route path="/admin/projects/:projectId" element={<AdminRoute><AdminProjectDetail /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/users/:userId" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
        <Route path="/admin/errors" element={<AdminRoute><AdminActivityErrors /></AdminRoute>} />
        <Route path="/admin/errors/:errorId" element={<AdminRoute><AdminErrorDetail /></AdminRoute>} />
        <Route path="/admin/error-buckets" element={<AdminRoute><AdminErrorBuckets /></AdminRoute>} />
        <Route path="/admin/error-buckets/:bucketId" element={<AdminRoute><AdminErrorBucketDetail /></AdminRoute>} />
        <Route path="/admin/bug-distribution" element={<AdminRoute><AdminBugDistribution /></AdminRoute>} />
        <Route path="/admin/evals" element={<AdminRoute><AdminEvals /></AdminRoute>} />
        <Route path="/admin/evals/:evalId" element={<AdminRoute><AdminEvalDetail /></AdminRoute>} />
        <Route path="/admin/critics" element={<AdminRoute><AdminCritics /></AdminRoute>} />
        <Route path="/admin/critics/:criticId" element={<AdminRoute><AdminCriticDetail /></AdminRoute>} />
        <Route path="/admin/critics/:criticId/days/:day" element={<AdminRoute><AdminCriticDay /></AdminRoute>} />
        <Route path="/admin/customers/:tier" element={<AdminRoute><AdminTierUsers /></AdminRoute>} />
      </Routes>
    </AppShell>
  )

  return (
    <EmbedContext.Provider value={isEmbedded}>
      <SegmentAuthBridge />
      <FullStoryAuthBridge />
      <PostHogAuthBridge />
      {routes}
      {/* A dismissible nudge shown when the account's credits hit zero on the ordinary viewing
          surfaces (project pages, insights, …), where the full-screen creation gate never appears.
          Renders nothing unless the user is out of credits, and suppresses itself where a gate
          already shows or would be unwelcome (see OutOfCreditsModal). */}
      <OutOfCreditsModal />
      {/* Shown once to a brand-new account, which was put on the Free plan at signup without ever being
          told. Renders nothing for everyone else (see WelcomeModal). */}
      <WelcomeModal />
      {/* Strip the ?checkout= param that billing redirects carry so it doesn't linger in the URL.
          SubscriptionGate has its own strip for returns that land on the gate, but a successful
          checkout gives the user credits → the gate never mounts → this catches that case. */}
      <StripCheckoutParam />
      {/* Rendered outside `routes` so a user flagged for abuse is locked out on EVERY surface — the
          app shell, the embedded iframe shell, and the invite landing alike. Renders nothing unless
          the current user is flagged. */}
      <AbuseOverlay />
    </EmbedContext.Provider>
  )
}
